// Tier-System + reichhaltige Metadaten je Standort.
// Tier 1: Top-Städte + alle 23 Wiener Bezirke + Bundesländer  → 1000–1200 Wörter, kuratierter Lokal-Content
// Tier 2: Bezirkshauptstädte + große Städte → 800–1000 Wörter, ausführlicher Lokal-Content
// Tier 3: alle übrigen Hauptstädte/Städte → 600–800 Wörter, solider Lokal-Content
export type Tier = 1 | 2 | 3;

export type CityMeta = {
  /** prägende Stadtteile/Ortsteile zum Verlinken im Text */
  stadtteile?: string[];
  /** lokale Charakteristik (1 Satz) */
  charakter?: string;
  /** Parkpickerl / Halteverbot Hinweis */
  parking?: string;
  /** Wohngebäude-Mix-Hinweis */
  bauten?: string;
  /** Verkehr / Anfahrt */
  anfahrt?: string;
  /** lokales Preisniveau-Faktor (1.0 = Standard, 1.15 = teurer Großstadt, 0.9 = günstiger) */
  preisFaktor?: number;
  /** Nachbarorte für interne Verlinkung */
  nachbarn?: string[];
};

export const tierBySlug: Record<string, Tier> = {
  // ===== Tier 1: Bundesländer =====
  wien: 1, niederoesterreich: 1, oberoesterreich: 1, steiermark: 1,
  tirol: 1, kaernten: 1, "salzburg-land": 1, vorarlberg: 1, burgenland: 1,

  // ===== Tier 1: Top-Städte (15) =====
  graz: 1, linz: 1, salzburg: 1, innsbruck: 1, klagenfurt: 1,
  villach: 1, wels: 1, "st-poelten": 1, dornbirn: 1, "wiener-neustadt": 1,
  steyr: 1, feldkirch: 1, bregenz: 1, leoben: 1, klosterneuburg: 1,

  // ===== Tier 1: alle 23 Wiener Bezirke =====
  "wien-1-innere-stadt": 1, "wien-2-leopoldstadt": 1, "wien-3-landstrasse": 1,
  "wien-4-wieden": 1, "wien-5-margareten": 1, "wien-6-mariahilf": 1,
  "wien-7-neubau": 1, "wien-8-josefstadt": 1, "wien-9-alsergrund": 1,
  "wien-10-favoriten": 1, "wien-11-simmering": 1, "wien-12-meidling": 1,
  "wien-13-hietzing": 1, "wien-14-penzing": 1, "wien-15-rudolfsheim-fuenfhaus": 1,
  "wien-16-ottakring": 1, "wien-17-hernals": 1, "wien-18-waehring": 1,
  "wien-19-doebling": 1, "wien-20-brigittenau": 1, "wien-21-floridsdorf": 1,
  "wien-22-donaustadt": 1, "wien-23-liesing": 1,

  // ===== Tier 2: starke Bezirksstädte =====
  baden: 2, moedling: 2, neunkirchen: 2, "krems-an-der-donau": 2, amstetten: 2,
  kapfenberg: 2, hallein: 2, traun: 2, lustenau: 2, kufstein: 2,
  tulln: 2, hohenems: 2, "spittal-an-der-drau": 2, "braunau-am-inn": 2, bludenz: 2,
  mattersburg: 2, "bad-voeslau": 2, traiskirchen: 2, "brunn-am-gebirge": 2,
  perchtoldsdorf: 2, ebreichsdorf: 2, ternitz: 2,

  // ===== Tier 2: Bundeslandhauptstadt Burgenland =====
  eisenstadt: 2,
};

export function tierOf(slug: string): Tier {
  return tierBySlug[slug] ?? 3;
}

// Kuratierte Metadaten für Tier-1-Städte und prominente Bezirke.
// Andere Standorte erhalten generierten Lokal-Content aus Region/Bundesland/Population.
export const cityMeta: Record<string, CityMeta> = {
  // ===== Wiener Bezirke =====
  "wien-1-innere-stadt": {
    stadtteile: ["Stephansplatz", "Schottenring", "Graben", "Hoher Markt"],
    charakter: "historisches UNESCO-Welterbe mit Palais, Altbauten und engen Gassen",
    parking: "fast flächendeckend Halteverbot nötig – wir beantragen die Kurzparkzonen-Aufhebung bei der MA 46",
    bauten: "fast ausschließlich Altbau mit Holzkastenfenstern, Stuck und schmalen Stiegenhäusern; viele Lifte nur bis Halbstock",
    anfahrt: "Anfahrt in die Fußgängerzone nur mit Sondergenehmigung – Verladung oft am Ring oder Franz-Josefs-Kai",
    preisFaktor: 1.2,
    nachbarn: ["wien-2-leopoldstadt", "wien-3-landstrasse", "wien-9-alsergrund"],
  },
  "wien-2-leopoldstadt": {
    stadtteile: ["Karmeliterviertel", "Praterstern", "Stuwerviertel", "Volkertviertel"],
    charakter: "lebendiger Mix aus Altbau-Gründerzeit und modernen Neubauten am Donaukanal",
    parking: "Parkpickerl-Zone – Halteverbot meist nötig, kurze Vorlaufzeit von 14 Tagen einplanen",
    bauten: "Gründerzeit-Zinshäuser im Karmeliterviertel, Neubau mit Aufzug rund um Praterstern",
    anfahrt: "gut erreichbar über Praterstraße und Lassallestraße",
    preisFaktor: 1.1,
    nachbarn: ["wien-1-innere-stadt", "wien-20-brigittenau", "wien-3-landstrasse"],
  },
  "wien-3-landstrasse": {
    stadtteile: ["Rennweg", "Erdberg", "Fasanviertel"],
    charakter: "diplomatisches Botschaftsviertel mit Palais und modernen Bürotürmen",
    parking: "Parkpickerl-Zone – Halteverbot besonders rund um Landstraßer Hauptstraße erforderlich",
    bauten: "Palais-Altbauten am Rennweg, Plattenbau in Erdberg, Neubau bei der Wien-Mitte",
    anfahrt: "U3, U4 und S-Bahn-Knoten – Anlieferung über Schlachthausgasse oder Marxergasse",
    preisFaktor: 1.1,
    nachbarn: ["wien-1-innere-stadt", "wien-11-simmering", "wien-4-wieden"],
  },
  "wien-4-wieden": {
    stadtteile: ["Freihaus", "Karlsplatz", "Schaumburgergrund"],
    charakter: "kleiner, dichter Innenstadt-Bezirk mit hoher Altbau-Dichte",
    parking: "Parkpickerl pflichtig – Halteverbotszone meist nötig, enge Querstraßen",
    bauten: "fast ausschließlich Gründerzeit-Altbau, oft mit Mezzanin und ohne Lift",
    anfahrt: "U1 Taubstummengasse, U4 Karlsplatz – Anlieferung via Wiedner Hauptstraße",
    preisFaktor: 1.15,
    nachbarn: ["wien-5-margareten", "wien-6-mariahilf", "wien-1-innere-stadt"],
  },
  "wien-5-margareten": {
    stadtteile: ["Margaretengürtel", "Schlossquadrat", "Bacherplatz"],
    charakter: "dichter Wohnbezirk mit Altbauten und Gemeindebauten",
    parking: "Parkpickerl ganztägig – Halteverbot meist Voraussetzung",
    bauten: "Gründerzeit-Zinshäuser sowie typische Wiener Gemeindebauten (Reumannhof, Metzleinstaler Hof)",
    anfahrt: "U4 Pilgramgasse, Straßenbahnlinien 1, 62, 65 – Hauptzufahrt Reinprechtsdorfer Straße",
    preisFaktor: 1.1,
    nachbarn: ["wien-4-wieden", "wien-6-mariahilf", "wien-12-meidling"],
  },
  "wien-6-mariahilf": {
    stadtteile: ["Gumpendorf", "Magdalenengrund", "Windmühle"],
    charakter: "Einkaufsbezirk rund um die Mariahilfer Straße mit engen Gassen dahinter",
    parking: "Mariahilfer Straße komplett Fußgängerzone – Halteverbot in den Querstraßen Pflicht",
    bauten: "Altbau dominant, einige Dachgeschossausbauten mit Lift",
    anfahrt: "U3 Neubaugasse, U4 Pilgramgasse – Anlieferung über Gumpendorfer Straße",
    preisFaktor: 1.15,
    nachbarn: ["wien-7-neubau", "wien-4-wieden", "wien-15-rudolfsheim-fuenfhaus"],
  },
  "wien-7-neubau": {
    stadtteile: ["Spittelberg", "Neubau", "Schottenfeld"],
    charakter: "kreativer Innenstadtbezirk mit kleinteiligen Altbauten und vielen Galerien",
    parking: "Parkpickerl-Zone – fast immer Halteverbot nötig, oft nur kleine LKW (7,5 t) zulässig",
    bauten: "Biedermeier- und Gründerzeit-Altbau, viele Hinterhof-Wohnungen ohne Lift",
    anfahrt: "U3 Volkstheater/Neubaugasse – Zufahrt häufig über schmale Einbahnen",
    preisFaktor: 1.15,
    nachbarn: ["wien-6-mariahilf", "wien-8-josefstadt", "wien-15-rudolfsheim-fuenfhaus"],
  },
  "wien-8-josefstadt": {
    stadtteile: ["Lerchenfeld", "Strozzigrund", "Alsergrund-Nahbereich"],
    charakter: "kleinster Bezirk Wiens, vornehm-bürgerlich mit Theater- und Universitätsnähe",
    parking: "Parkpickerl flächendeckend – Halteverbot pflichtig",
    bauten: "Gründerzeit-Altbau mit hochwertigem Innenausbau, häufig Stuck",
    anfahrt: "Straßenbahn 5, 33, 43, 44 – enge Anlieferzonen über Lerchenfelder Straße",
    preisFaktor: 1.15,
    nachbarn: ["wien-1-innere-stadt", "wien-7-neubau", "wien-9-alsergrund"],
  },
  "wien-9-alsergrund": {
    stadtteile: ["Servitenviertel", "Spitalsviertel", "Liechtensteinviertel"],
    charakter: "Universitäts- und Spitalsbezirk mit gehobener Bausubstanz",
    parking: "Parkpickerl pflichtig – Halteverbot rund um AKH und Liechtensteinpark erforderlich",
    bauten: "Gründerzeit-Altbau hoher Qualität, einige Stadtpalais",
    anfahrt: "U2 Schottentor, U6 Spitalgasse – Anlieferung oft via Währinger Straße",
    preisFaktor: 1.15,
    nachbarn: ["wien-1-innere-stadt", "wien-8-josefstadt", "wien-18-waehring", "wien-19-doebling"],
  },
  "wien-10-favoriten": {
    stadtteile: ["Reumannplatz", "Quellenstraße", "Sonnwendviertel", "Oberlaa"],
    charakter: "bevölkerungsreichster Bezirk Wiens, Mix aus Altbau, Gemeindebau und Neubau am Hauptbahnhof",
    parking: "Parkpickerl-Zone – in den Neubaugebieten oft mit Tiefgaragenzufahrt, im Altbau Halteverbot nötig",
    bauten: "Reumannhof-artige Gemeindebauten, Gründerzeit-Altbau, moderne Neubauten am Sonnwendviertel mit Lift",
    anfahrt: "U1 Reumannplatz/Oberlaa, S-Bahn-Hauptbahnhof – breite Zufahrt über Favoritenstraße",
    preisFaktor: 1.0,
    nachbarn: ["wien-4-wieden", "wien-11-simmering", "wien-23-liesing"],
  },
  "wien-11-simmering": {
    stadtteile: ["Simmeringer Platz", "Kaiserebersdorf", "Albern"],
    charakter: "Industrie- und Wohnbezirk im Südosten mit großzügigen Zufahrtsstraßen",
    parking: "in Wohnzonen Parkpickerl, an Industriestandorten i. d. R. frei zugänglich",
    bauten: "Mischung aus Gemeindebau, Altbau und Einfamilienhäusern in Kaiserebersdorf",
    anfahrt: "U3 Simmering, A4 Ostautobahn – gut erreichbar für 7,5-t- und 12-t-LKW",
    preisFaktor: 0.95,
    nachbarn: ["wien-3-landstrasse", "wien-10-favoriten"],
  },
  "wien-12-meidling": {
    stadtteile: ["Tivoli", "Wilhelmsdorf", "Hetzendorf"],
    charakter: "klassischer Arbeiterbezirk mit vielen Gemeindebauten und Gründerzeit-Altbau",
    parking: "Parkpickerl-Zone – Halteverbot rund um Meidlinger Hauptstraße meist nötig",
    bauten: "Altbau dominant, dazu Reichenberger Hof und weitere Gemeindebauten",
    anfahrt: "U6 Philadelphiabrücke/Niederhofstraße – LKW-Zufahrt über Schönbrunner Straße",
    preisFaktor: 1.0,
    nachbarn: ["wien-13-hietzing", "wien-5-margareten", "wien-23-liesing"],
  },
  "wien-13-hietzing": {
    stadtteile: ["Schönbrunn", "Lainz", "Speising", "Ober Sankt Veit"],
    charakter: "grüner Villen-Bezirk mit Schloss Schönbrunn und großen Einfamilienhäusern",
    parking: "Parkpickerl meist nur in der Ostzone – im Cottageviertel oft freie Zufahrt",
    bauten: "Villen mit Garten in Lainz/Speising, Altbau-Reihenhäuser, Schönbrunn-Nähe historisch",
    anfahrt: "U4 Hietzing, A1 West – breite Zufahrt für Möbel-LKW kein Problem",
    preisFaktor: 1.1,
    nachbarn: ["wien-14-penzing", "wien-12-meidling", "wien-23-liesing"],
  },
  "wien-14-penzing": {
    stadtteile: ["Hütteldorf", "Penzing", "Baumgarten", "Hadersdorf"],
    charakter: "weitläufiger Westbezirk mit Wienerwald-Anschluss, gemischte Bebauung",
    parking: "Parkpickerl nur in der Ostzone – im Westen oft kostenlose Anrainerzonen",
    bauten: "Altbau am Hauptmühlbach, Einfamilienhäuser Richtung Wienerwald",
    anfahrt: "U4 Hütteldorf, A1 – LKW-Zufahrt über Linzer Straße",
    preisFaktor: 1.0,
    nachbarn: ["wien-13-hietzing", "wien-15-rudolfsheim-fuenfhaus", "wien-16-ottakring"],
  },
  "wien-15-rudolfsheim-fuenfhaus": {
    stadtteile: ["Schwendermarkt", "Märzpark", "Reindorf"],
    charakter: "dicht bebauter Bezirk westlich des Gürtels mit hoher Altbau-Quote",
    parking: "Parkpickerl-Zone – Halteverbot fast immer Pflicht, enge Gassen",
    bauten: "Gründerzeit-Altbau, viele kleine Wohnungen ohne Lift",
    anfahrt: "U3 Westbahnhof, S-Bahn Westbahnhof – Anlieferung via Mariahilfer Straße",
    preisFaktor: 1.05,
    nachbarn: ["wien-6-mariahilf", "wien-7-neubau", "wien-14-penzing", "wien-16-ottakring"],
  },
  "wien-16-ottakring": {
    stadtteile: ["Brunnenmarkt", "Sandleiten", "Ottakring"],
    charakter: "Arbeiter- und Migrationsbezirk mit lebendiger Markthalle und Yppenplatz",
    parking: "Parkpickerl-Zone – Halteverbot besonders rund um Brunnenmarkt nötig",
    bauten: "Gründerzeit-Altbau, einige große Gemeindebauten (Sandleiten)",
    anfahrt: "U3 Ottakring, Straßenbahn 2, 44, 46 – Hauptachse Thaliastraße",
    preisFaktor: 0.95,
    nachbarn: ["wien-17-hernals", "wien-15-rudolfsheim-fuenfhaus", "wien-14-penzing"],
  },
  "wien-17-hernals": {
    stadtteile: ["Dornbach", "Neuwaldegg", "Hernals"],
    charakter: "ruhiger Bezirk am Wienerwald-Hang mit Mischung aus Altbau und Einfamilienhäusern",
    parking: "Parkpickerl in den Tallagen – am Hang oft frei zugänglich",
    bauten: "Altbau im Tal, Villen und Reihenhäuser am Hang",
    anfahrt: "Straßenbahn 9, 43 – breite Zufahrt Hernalser Hauptstraße",
    preisFaktor: 1.0,
    nachbarn: ["wien-16-ottakring", "wien-18-waehring", "wien-9-alsergrund"],
  },
  "wien-18-waehring": {
    stadtteile: ["Cottageviertel", "Gersthof", "Pötzleinsdorf"],
    charakter: "nobles Cottageviertel mit Villen und gehobener Gründerzeit",
    parking: "Parkpickerl-Zone – im Cottageviertel meist mit breiter Garagenzufahrt",
    bauten: "Villen und Stadtpalais, gehobene Altbau-Wohnungen",
    anfahrt: "Straßenbahn 40, 41 – ruhige Anlieferung möglich",
    preisFaktor: 1.15,
    nachbarn: ["wien-9-alsergrund", "wien-19-doebling", "wien-17-hernals"],
  },
  "wien-19-doebling": {
    stadtteile: ["Heiligenstadt", "Grinzing", "Sievering", "Nußdorf", "Kahlenbergerdorf"],
    charakter: "grüner Villenbezirk mit Weinhauern, Karl-Marx-Hof und Hangwohnen",
    parking: "Parkpickerl nur in Teilen – am Hang oft frei",
    bauten: "Villen, der berühmte Karl-Marx-Hof, Hangwohnen-Reihenhäuser",
    anfahrt: "U4 Heiligenstadt, U6 Nußdorfer Straße – LKW-Zufahrt unproblematisch",
    preisFaktor: 1.15,
    nachbarn: ["wien-18-waehring", "wien-20-brigittenau", "wien-21-floridsdorf"],
  },
  "wien-20-brigittenau": {
    stadtteile: ["Brigittenau", "Wallensteinplatz", "Hannovermarkt"],
    charakter: "dicht bebauter Bezirk zwischen Donau und Donaukanal",
    parking: "Parkpickerl-Zone – Halteverbot fast immer nötig",
    bauten: "Gemeindebauten (Friedrich-Engels-Hof) und Gründerzeit-Altbau",
    anfahrt: "U6 Jägerstraße/Dresdner Straße, S-Bahn Traisengasse",
    preisFaktor: 1.0,
    nachbarn: ["wien-2-leopoldstadt", "wien-9-alsergrund", "wien-19-doebling"],
  },
  "wien-21-floridsdorf": {
    stadtteile: ["Großjedlersdorf", "Strebersdorf", "Floridsdorfer Markt", "Stammersdorf"],
    charakter: "großflächiger Bezirk nördlich der Donau mit Einfamilienhäusern und Plattenbau",
    parking: "Parkpickerl in zentralen Lagen – am Rand kostenlos",
    bauten: "Großfeldsiedlung (Plattenbau mit Lift), Einfamilienhäuser, Reihenhaussiedlungen",
    anfahrt: "U6 Floridsdorf, A22 Donauuferautobahn – ideal für große LKW",
    preisFaktor: 0.95,
    nachbarn: ["wien-22-donaustadt", "wien-20-brigittenau", "wien-19-doebling"],
  },
  "wien-22-donaustadt": {
    stadtteile: ["Kaisermühlen", "Aspern", "Stadlau", "Hirschstetten"],
    charakter: "größter Bezirk Wiens mit Aspern Seestadt, UNO-City und viel Neubau",
    parking: "in der Seestadt Tiefgaragen – sonst meist frei",
    bauten: "moderner Neubau mit Lift, Donau-City Hochhäuser, Einfamilienhäuser in Aspern",
    anfahrt: "U1, U2, A22, A23 – beste LKW-Anbindung Wiens",
    preisFaktor: 0.95,
    nachbarn: ["wien-21-floridsdorf", "wien-2-leopoldstadt", "wien-11-simmering"],
  },
  "wien-23-liesing": {
    stadtteile: ["Atzgersdorf", "Mauer", "Inzersdorf", "Rodaun"],
    charakter: "Industrie- und Wohnbezirk im Süden mit großem Einfamilienhaus-Anteil",
    parking: "kaum Parkpickerl – meist frei zugängliche Zufahrt",
    bauten: "Einfamilienhäuser, Reihenhäuser, Industrieobjekte",
    anfahrt: "A2 Südautobahn, A23 – sehr LKW-freundlich",
    preisFaktor: 0.95,
    nachbarn: ["wien-13-hietzing", "wien-10-favoriten", "moedling", "voesendorf"],
  },

  // ===== Tier 1 Städte =====
  wien: {
    stadtteile: [
      "Innere Stadt", "Leopoldstadt", "Landstraße", "Wieden", "Margareten",
      "Mariahilf", "Neubau", "Josefstadt", "Alsergrund", "Favoriten",
      "Simmering", "Meidling", "Hietzing", "Penzing", "Rudolfsheim-Fünfhaus",
      "Ottakring", "Hernals", "Währing", "Döbling", "Brigittenau",
      "Floridsdorf", "Donaustadt", "Liesing"
    ],
    charakter: "Bundeshauptstadt mit 23 Bezirken, rund 2 Mio. Einwohnern und extrem heterogenem Wohnbau – vom UNESCO-Altbau im 1. Bezirk bis zum Plattenbau in Floridsdorf und der Seestadt Aspern",
    parking: "fast stadtweit Parkpickerl-Zone (Mo–Fr 9–22 Uhr) – Halteverbot zwingend nötig, wir beantragen die Kurzparkzonen-Aufhebung bei der MA 46 mit 14 Tagen Vorlauf",
    bauten: "innerhalb des Gürtels überwiegend Gründerzeit-Zinshäuser mit Holzkastenfenstern, Stuck und engen Stiegenhäusern (Lifte oft nur bis Halbstock); außerhalb Plattenbau, Genossenschaftsbauten und Neubau-Türme",
    anfahrt: "A1, A2, A4, A21, A22, A23 sowie der Gürtel als Hauptverteiler – Innenstadt mit LKW > 7,5 t nur mit Sondergenehmigung, Fußgängerzonen (Kärntner Straße, Graben) nur Ladezeiten 6–13 Uhr",
    preisFaktor: 1.15,
    nachbarn: [
      "wien-1-innere-stadt", "wien-2-leopoldstadt", "wien-7-neubau",
      "wien-10-favoriten", "wien-22-donaustadt", "klosterneuburg",
      "moedling", "schwechat"
    ],
  },
  graz: {
    stadtteile: ["Altstadt", "Lend", "Gries", "Geidorf", "St. Leonhard", "Eggenberg"],
    charakter: "zweitgrößte Stadt Österreichs und UNESCO-Welterbe-Altstadt",
    parking: "blaue/grüne Kurzparkzone in der Innenstadt – Halteverbot über die MA für Straßenpolizei möglich",
    bauten: "UNESCO-Altstadt mit Innenhöfen ohne LKW-Zufahrt, Gründerzeit-Bezirke Geidorf/St. Leonhard, Plattenbau in Andritz",
    anfahrt: "A2/A9-Knoten, gute Anbindung – Innenstadt-Zufahrt nur mit Sondergenehmigung",
    preisFaktor: 1.0,
    nachbarn: ["seiersberg-pirka", "leibnitz", "gleisdorf", "weiz"],
  },
  linz: {
    stadtteile: ["Innenstadt", "Urfahr", "Pöstlingberg", "Ebelsberg"],
    charakter: "drittgrößte Stadt Österreichs, Kulturhauptstadt mit Lentos und Ars Electronica Center",
    parking: "Kurzparkzone in der Innenstadt – Halteverbot über das Mobilitätsservice der Stadt Linz",
    bauten: "Gründerzeit in Innenstadt, Neubau in Urfahr, Industrieflächen am Hafen",
    anfahrt: "A1 Westautobahn, A7 Mühlkreisautobahn – sehr gute LKW-Erreichbarkeit",
    preisFaktor: 1.0,
    nachbarn: ["leonding", "traun", "ansfelden", "pasching", "asten"],
  },
  salzburg: {
    stadtteile: ["Altstadt", "Lehen", "Liefering", "Aigen", "Parsch"],
    charakter: "UNESCO-Welterbe-Altstadt, Festspielstadt mit engen Gassen am Mönchsberg",
    parking: "Altstadt fast vollständig Fußgängerzone – Halteverbot mit Stadtgemeinde Salzburg organisieren",
    bauten: "barocke Altstadt mit Innenhöfen, Gründerzeit in Lehen, Villen in Aigen",
    anfahrt: "A1, A10 – LKW-Anlieferung Innenstadt nur mit Sondergenehmigung der Stadt",
    preisFaktor: 1.15,
    nachbarn: ["hallein", "wals-siezenheim", "seekirchen-am-wallersee"],
  },
  innsbruck: {
    stadtteile: ["Altstadt", "Wilten", "Pradl", "Hötting", "Saggen"],
    charakter: "Tiroler Landeshauptstadt am Inn, umgeben von Karwendel und Patscherkofel",
    parking: "Kurzparkzonen in Innenstadt – Halteverbot über die Tiroler Landespolizeidirektion",
    bauten: "Altstadt mit Goldenem Dachl (sehr eng), Gründerzeit in Wilten, Hangwohnen in Hötting",
    anfahrt: "A12 Inntalautobahn, A13 Brennerautobahn – Innenstadt aufgrund Topografie herausfordernd",
    preisFaktor: 1.1,
    nachbarn: ["hall-in-tirol", "rum", "telfs", "schwaz"],
  },
  klagenfurt: {
    stadtteile: ["Innenstadt", "St. Veiter Vorstadt", "Welzenegg", "Annabichl"],
    charakter: "Kärntner Landeshauptstadt am Wörthersee",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Klagenfurt",
    bauten: "barocke Innenstadt, Plattenbau am Ostbahnhof, Einfamilienhäuser am Kreuzbergl",
    anfahrt: "A2 Südautobahn, A11 Karawankenautobahn",
    preisFaktor: 0.95,
    nachbarn: ["villach", "feldkirchen", "sankt-veit-an-der-glan", "ferlach"],
  },
  villach: {
    stadtteile: ["Innenstadt", "Lind", "Landskron", "Auen"],
    charakter: "zweitgrößte Kärntner Stadt an Drau und Faaker See",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Villach",
    bauten: "barocke Altstadt, Gründerzeit in der Innenstadt, Einfamilienhäuser in Landskron",
    anfahrt: "A2, A10, A11 – wichtiger Verkehrsknoten",
    preisFaktor: 0.95,
    nachbarn: ["klagenfurt", "hermagor", "spittal-an-der-drau", "wernberg"],
  },
  wels: {
    stadtteile: ["Innenstadt", "Lichtenegg", "Vogelweide", "Pernau"],
    charakter: "Messestadt und wichtiger Wirtschaftsstandort in Oberösterreich",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Wels",
    bauten: "barocke Altstadt mit engen Gassen, Gründerzeit in Vorstadt, Reihenhäuser in Pernau",
    anfahrt: "A8 Innkreisautobahn, A25 Welser Autobahn – ideale LKW-Anbindung",
    preisFaktor: 0.95,
    nachbarn: ["linz", "marchtrenk", "traun", "voecklabruck"],
  },
  "st-poelten": {
    stadtteile: ["Innenstadt", "Wagram", "Pottenbrunn", "Spratzern"],
    charakter: "Landeshauptstadt von Niederösterreich mit Kulturbezirk",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt St. Pölten",
    bauten: "barocke Innenstadt, Plattenbau und Reihenhäuser am Stadtrand",
    anfahrt: "A1 Westautobahn, S33 – sehr gut erreichbar",
    preisFaktor: 0.95,
    nachbarn: ["tulln", "krems-an-der-donau", "neulengbach", "purkersdorf"],
  },
  dornbirn: {
    stadtteile: ["Markt", "Hatlerdorf", "Oberdorf", "Rohrbach"],
    charakter: "größte Stadt Vorarlbergs, Textil- und Messestandort",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Dornbirn",
    bauten: "Gründerzeit im Markt, viele Einfamilienhäuser in Hatlerdorf und Oberdorf",
    anfahrt: "A14 Rheintalautobahn – sehr gute Erreichbarkeit",
    preisFaktor: 1.05,
    nachbarn: ["hohenems", "lustenau", "feldkirch", "hard"],
  },
  "wiener-neustadt": {
    stadtteile: ["Innenstadt", "Josefstadt", "Föhrenwald", "Civitas Nova", "Ungarviertel", "Neudörfl-Grenze"],
    charakter: "zweitgrößte Stadt Niederösterreichs, Industrie- und Militärstadt mit Theresianischer Militärakademie",
    parking: "Kurzparkzonen in Innenstadt und rund um den Hauptplatz – Halteverbot über das Bürgerservice der Stadt Wiener Neustadt (Vorlauf 14 Tage)",
    bauten: "Gründerzeit-Zinshäuser in der Innenstadt, große Genossenschaftsbauten in Josefstadt und Föhrenwald, Einfamilienhäuser Richtung Bad Fischau und Neudörfl",
    anfahrt: "A2 Südautobahn (Abfahrt Wr. Neustadt Nord/Süd), S4, direkter S-Bahn-Anschluss – von Wien in 45 Minuten",
    preisFaktor: 0.95,
    nachbarn: ["neunkirchen", "baden", "ebreichsdorf", "ternitz", "mattersburg", "eisenstadt", "bad-voeslau", "traiskirchen"],
  },
  steyr: {
    stadtteile: ["Stadt", "Steyrdorf", "Münichholz", "Resthof"],
    charakter: "historische Eisenstadt am Zusammenfluss von Steyr und Enns",
    parking: "Kurzparkzonen – Halteverbot über die Stadt Steyr",
    bauten: "gotische Altstadt mit engen Gassen, Stadl-Bauten in Steyrdorf",
    anfahrt: "A1, B115 – Innenstadt-Zufahrt aufgrund Topografie eingeschränkt",
    preisFaktor: 0.95,
    nachbarn: ["sankt-valentin", "enns", "sierning", "perg"],
  },
  feldkirch: {
    stadtteile: ["Innenstadt", "Altenstadt", "Tisis", "Tosters"],
    charakter: "historische Vorarlberger Stadt mit Schattenburg",
    parking: "Kurzparkzonen – Halteverbot über die Stadt Feldkirch",
    bauten: "mittelalterliche Altstadt mit Lauben, Einfamilienhäuser in Altenstadt",
    anfahrt: "A14, S16 – grenznah zu Liechtenstein und der Schweiz",
    preisFaktor: 1.05,
    nachbarn: ["rankweil", "goetzis", "hohenems", "bludenz"],
  },
  bregenz: {
    stadtteile: ["Stadt", "Oberstadt", "Vorkloster", "Rieden-Vorkloster"],
    charakter: "Vorarlberger Landeshauptstadt am Bodensee, Festspielstadt",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Bregenz",
    bauten: "Oberstadt mit historischer Bausubstanz (eng), Gründerzeit in Vorkloster",
    anfahrt: "A14 – sehr gute Anbindung",
    preisFaktor: 1.1,
    nachbarn: ["hard", "lauterach", "wolfurt", "hoechst", "dornbirn"],
  },
  leoben: {
    stadtteile: ["Innenstadt", "Donawitz", "Göss", "Judendorf"],
    charakter: "Bergbau- und Universitätsstadt in der Obersteiermark",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "Gründerzeit-Innenstadt, Werkssiedlungen in Donawitz",
    anfahrt: "S6 Semmering Schnellstraße – gute Anbindung",
    preisFaktor: 0.9,
    nachbarn: ["trofaiach", "bruck-an-der-mur", "knittelfeld", "judenburg"],
  },
  klosterneuburg: {
    stadtteile: ["Klosterneuburg", "Kierling", "Weidling", "Höflein"],
    charakter: "Stiftsstadt am Wienerwald, beliebte Wohnlage am Stadtrand Wiens",
    parking: "Kurzparkzonen rund um das Stift – sonst meist frei zugänglich",
    bauten: "historische Bausubstanz am Stift, viele Einfamilienhäuser und Villen am Hang",
    anfahrt: "B14, S5 – Wien in 20 Minuten erreichbar",
    preisFaktor: 1.1,
    nachbarn: ["wien-19-doebling", "korneuburg", "tulln", "purkersdorf"],
  },

  // ===== Tier 2 (kompakter, aber kuratiert) =====
  baden: {
    stadtteile: ["Innenstadt", "Weikersdorf", "Leesdorf", "Mitterberg", "Rauhenstein"],
    charakter: "klassizistische Kurstadt südlich von Wien (UNESCO-Welterbe der Great Spa Towns of Europe)",
    parking: "Kurzparkzonen Innenstadt und Kurpark-Bereich – Halteverbot über das Bürgerbüro der Stadtgemeinde Baden (Vorlauf 10–14 Tage)",
    bauten: "Biedermeier-Villen und Kurhotels rund um Kurpark und Frauenbad, Gründerzeit an der Wiener Straße, Einfamilienhäuser in Weikersdorf und Rauhenstein",
    anfahrt: "A2 Südautobahn (Abfahrt Baden), Badner Bahn – 25 Minuten nach Wien",
    preisFaktor: 1.05,
    nachbarn: ["moedling", "bad-voeslau", "traiskirchen", "berndorf", "wiener-neustadt", "ebreichsdorf"],
  },
  moedling: {
    stadtteile: ["Stadt", "Vorder Brühl", "Klausen"],
    charakter: "südliche Gartenstadt am Wienerwald",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "Gründerzeit-Villen, Einfamilienhäuser, viele Neubauten am Stadtrand",
    anfahrt: "A2, A21 – 20 Min nach Wien",
    preisFaktor: 1.05,
    nachbarn: ["baden", "perchtoldsdorf", "brunn-am-gebirge", "maria-enzersdorf", "wien-23-liesing"],
  },
  neunkirchen: {
    stadtteile: ["Innenstadt", "Mühlfeld", "Peisching", "Sonnleiten"],
    charakter: "Industrie- und Bezirksstadt am südlichen Rand des Wiener Beckens, wichtiger Verkehrsknoten Richtung Semmering",
    parking: "Kurzparkzonen am Hauptplatz und in der Triester Straße – Halteverbot über die Stadtgemeinde Neunkirchen (Vorlauf ca. 10 Tage)",
    bauten: "Gründerzeit-Zinshäuser in der Innenstadt, große Genossenschaftsbauten und Plattenbauten in Mühlfeld, Einfamilienhäuser in Peisching und Sonnleiten",
    anfahrt: "A2 Südautobahn (Abfahrt Seebenstein/Neunkirchen), S6 Semmering Schnellstraße – 50 Minuten nach Wien",
    preisFaktor: 0.9,
    nachbarn: ["wiener-neustadt", "ternitz", "baden", "mattersburg"],
  },
  "krems-an-der-donau": {
    stadtteile: ["Stadt", "Stein", "Lerchenfeld", "Egelsee"],
    charakter: "Wachau-Tor mit historischer Altstadt und Weinbau",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Krems",
    bauten: "mittelalterliche Altstadt in Stein, Gründerzeit in Krems-Stadt",
    anfahrt: "S5, A1 – Wien in 1 Stunde",
    preisFaktor: 0.95,
    nachbarn: ["melk", "tulln", "horn", "stockerau"],
  },
  amstetten: {
    charakter: "Bezirkshauptstadt im Mostviertel",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "Stadtkern mit historischer Bausubstanz, sonst Einfamilienhäuser",
    anfahrt: "A1 Westautobahn",
    preisFaktor: 0.9,
    nachbarn: ["sankt-valentin", "waidhofen-an-der-ybbs"],
  },
  kapfenberg: {
    charakter: "Industriestadt der Obersteiermark",
    parking: "wenig Kurzparkzonen – meist frei",
    bauten: "Werkssiedlungen, Plattenbau, Einfamilienhäuser",
    anfahrt: "S6 – gute Anbindung",
    preisFaktor: 0.9,
    nachbarn: ["bruck-an-der-mur", "leoben", "muerzzuschlag"],
  },
  hallein: {
    stadtteile: ["Altstadt", "Burgfried", "Rif"],
    charakter: "historische Salinenstadt südlich von Salzburg",
    parking: "Kurzparkzonen in der Altstadt – Halteverbot über die Stadt Hallein",
    bauten: "mittelalterliche Altstadt mit engen Gassen, Einfamilienhäuser am Stadtrand",
    anfahrt: "A10 Tauernautobahn – 15 Min nach Salzburg",
    preisFaktor: 1.05,
    nachbarn: ["salzburg", "bischofshofen"],
  },
  traun: {
    charakter: "Wohn- und Industriestadt im Linzer Zentralraum",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "Einfamilienhäuser, Reihenhäuser, einige Hochhäuser",
    anfahrt: "A1, A25 – sehr gute LKW-Anbindung",
    preisFaktor: 0.95,
    nachbarn: ["linz", "leonding", "ansfelden", "pasching"],
  },
  lustenau: {
    charakter: "größte Marktgemeinde Vorarlbergs an der Schweizer Grenze",
    parking: "wenig Kurzparkzonen",
    bauten: "viele Einfamilienhäuser, einige Gewerbeflächen",
    anfahrt: "A14, Grenzübergang Schweiz",
    preisFaktor: 1.05,
    nachbarn: ["dornbirn", "hohenems", "hoechst"],
  },
  kufstein: {
    stadtteile: ["Stadt", "Endach", "Sparchen"],
    charakter: "Tiroler Festungsstadt am Inn",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadt Kufstein",
    bauten: "historische Altstadt mit engen Gassen, Einfamilienhäuser am Stadtrand",
    anfahrt: "A12 Inntalautobahn – grenznah zu Bayern",
    preisFaktor: 1.0,
    nachbarn: ["woergl", "sankt-johann-in-tirol", "jenbach"],
  },
  tulln: {
    charakter: "Donaustadt nordwestlich von Wien (\"Stadt der Blumen\")",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "historische Bausubstanz Innenstadt, Einfamilienhäuser am Stadtrand",
    anfahrt: "S5, A22 – 30 Min nach Wien",
    preisFaktor: 0.95,
    nachbarn: ["klosterneuburg", "krems-an-der-donau", "stockerau"],
  },
  hohenems: {
    charakter: "Vorarlberger Schlossstadt im Rheintal",
    parking: "Kurzparkzonen",
    bauten: "historischer Markt, Einfamilienhäuser",
    anfahrt: "A14",
    preisFaktor: 1.05,
    nachbarn: ["dornbirn", "feldkirch", "lustenau", "goetzis"],
  },
  "spittal-an-der-drau": {
    charakter: "Bezirksstadt im Oberkärntner Drautal",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "Schloss Porcia, Gründerzeit, Einfamilienhäuser",
    anfahrt: "A10",
    preisFaktor: 0.9,
    nachbarn: ["villach", "klagenfurt"],
  },
  "braunau-am-inn": {
    charakter: "Bezirksstadt im Innviertel an der bayerischen Grenze",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "historische Altstadt mit Stadtplatz, Reihenhäuser, Einfamilienhäuser",
    anfahrt: "B147, grenznah zu Bayern",
    preisFaktor: 0.9,
    nachbarn: ["ried-im-innkreis"],
  },
  bludenz: {
    charakter: "Vorarlberger \"Alpenstadt\" am Eingang zu fünf Tälern",
    parking: "Kurzparkzonen Innenstadt",
    bauten: "historische Altstadt, Einfamilienhäuser",
    anfahrt: "A14, S16",
    preisFaktor: 1.0,
    nachbarn: ["feldkirch", "nenzing"],
  },

  // ===== Landeshauptstadt Burgenland =====
  eisenstadt: {
    stadtteile: ["Innenstadt", "Oberberg", "Unterberg", "Kleinhöflein", "Sankt Georgen"],
    charakter: "kleinste Landeshauptstadt Österreichs, Sitz des Schlosses Esterházy und Zentrum des Nordburgenlands",
    parking: "Kurzparkzonen in der Innenstadt und rund um das Schloss – Halteverbot über das Bürgerservice der Landeshauptstadt Eisenstadt (Vorlauf ca. 14 Tage)",
    bauten: "barocke Innenstadt rund um Hauptstraße und Schloss Esterházy, Winzerhäuser in Kleinhöflein und Sankt Georgen, Einfamilienhäuser und Reihenhäuser am Stadtrand",
    anfahrt: "A3 Nordostautobahn, S31 Burgenland Schnellstraße – 50 Minuten nach Wien",
    preisFaktor: 0.95,
    nachbarn: ["mattersburg", "neusiedl-am-see", "rust", "wiener-neustadt"],
  },
  mattersburg: {
    stadtteile: ["Zentrum", "Walbersdorf", "Bahnhofsviertel"],
    charakter: "Bezirkshauptstadt im Nordburgenland zwischen Rosalien- und Leithagebirge, wichtiger Wirtschafts- und Bildungsstandort",
    parking: "Kurzparkzone rund um Hauptplatz und Bahnhof – Halteverbot über die Stadtgemeinde Mattersburg (Vorlauf 10–14 Tage)",
    bauten: "Gründerzeit-Häuser im Zentrum, Genossenschaftsbauten im Bahnhofsviertel, überwiegend Einfamilienhäuser in Walbersdorf und Richtung Forchtenstein",
    anfahrt: "S31 Burgenland Schnellstraße, direkter Bahn-Anschluss – 45 Minuten nach Wien, 15 Minuten nach Wiener Neustadt und Eisenstadt",
    preisFaktor: 0.9,
    nachbarn: ["eisenstadt", "wiener-neustadt", "neusiedl-am-see"],
  },
  ebreichsdorf: {
    charakter: "wachsende Stadtgemeinde im Industrieviertel zwischen Baden und Wiener Neustadt",
    parking: "wenige Kurzparkzonen – Halteverbot über die Stadtgemeinde Ebreichsdorf",
    bauten: "historischer Ortskern, viele Einfamilienhäuser und Neubausiedlungen",
    anfahrt: "A3, S4 – 30 Minuten nach Wien",
    preisFaktor: 0.9,
    nachbarn: ["baden", "traiskirchen", "wiener-neustadt", "eisenstadt"],
  },
  ternitz: {
    charakter: "Industriestadt südlich von Neunkirchen im Schwarzatal",
    parking: "kaum Kurzparkzonen – Halteverbot über die Stadtgemeinde Ternitz",
    bauten: "Werkssiedlungen, Genossenschaftsbauten, Einfamilienhäuser",
    anfahrt: "S6 Semmering Schnellstraße",
    preisFaktor: 0.85,
    nachbarn: ["neunkirchen", "wiener-neustadt"],
  },
  "bad-voeslau": {
    charakter: "Kurstadt südlich von Baden mit bekanntem Thermalbad und Weinbau",
    parking: "Kurzparkzonen Innenstadt – Halteverbot über die Stadtgemeinde Bad Vöslau",
    bauten: "Villen aus der Kaiserzeit, Einfamilienhäuser, Reihenhäuser",
    anfahrt: "A2, Badner Bahn – 30 Min nach Wien",
    preisFaktor: 1.0,
    nachbarn: ["baden", "berndorf", "wiener-neustadt"],
  },
  traiskirchen: {
    charakter: "Weinbau- und Industriestadt südlich von Wien",
    parking: "Kurzparkzonen in Ortszentrum – Halteverbot über die Stadtgemeinde Traiskirchen",
    bauten: "Winzerhäuser, Einfamilienhäuser, Neubausiedlungen",
    anfahrt: "A2, Badner Bahn – 25 Min nach Wien",
    preisFaktor: 0.95,
    nachbarn: ["baden", "bad-voeslau", "ebreichsdorf"],
  },
  "brunn-am-gebirge": {
    charakter: "Gewerbestandort am Wienerwaldrand, südlich an Wien angrenzend",
    parking: "kaum Kurzparkzonen",
    bauten: "Einfamilienhäuser, Reihenhäuser, große Gewerbeflächen (Campus 21)",
    anfahrt: "A2, A21 – direkt an Wien 23 anschließend",
    preisFaktor: 1.05,
    nachbarn: ["moedling", "perchtoldsdorf", "wien-23-liesing", "maria-enzersdorf"],
  },
  perchtoldsdorf: {
    charakter: "wohlhabende Marktgemeinde am Südrand Wiens mit historischem Wehrturm",
    parking: "Kurzparkzonen im Ortszentrum",
    bauten: "Heurigen-Häuser im Ortskern, Villen und Einfamilienhäuser am Hang",
    anfahrt: "B12 – direkt an Wien 23 angrenzend",
    preisFaktor: 1.1,
    nachbarn: ["wien-23-liesing", "moedling", "brunn-am-gebirge"],
  },
};
